import React, { useState } from 'react';
import { Typography, TextField, IconButton, Tooltip, InputAdornment, Grid } from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy } from '@mui/icons-material';
import { COLOR_GREY_600 } from 'src/utils/colors';
import KeyIcon from '@mui/icons-material/Key';
import CustomBasicCard from 'src/App/Dashboards/Market/Components/Cards/CustomBasicCard';

interface ApiKeyDisplayCardProps {
  apiKey: string;
  onCopy?: () => void;
  onToggleVisibility?: () => void;
}

const ApiKeyDisplayCard: React.FC<ApiKeyDisplayCardProps> = ({
  apiKey,
  onCopy,
  onToggleVisibility,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleToggleVisibility = () => {
    setIsVisible(!isVisible);
    onToggleVisibility?.();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy API key:', err);
    }
  };

  // Show the "sk-airbtics-live-" part, mask the rest unless visible
  const keyPrefix = 'sk-airbtics-live-';
  const prefixLength = keyPrefix.length;
  const displayKey = isVisible
    ? apiKey
    : apiKey.startsWith(keyPrefix)
    ? keyPrefix + '•'.repeat(apiKey.length - prefixLength)
    : '•'.repeat(apiKey.length);

  const content = (
    <>
      <Grid item xs={12}>
        <TextField
          fullWidth
          value={displayKey}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={isVisible ? 'Hide API key' : 'Show API key'}>
                  <IconButton onClick={handleToggleVisibility} edge="end">
                    {isVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={copied ? 'Copied!' : 'Copy API key'}>
                  <IconButton onClick={handleCopy} edge="end">
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'grey.50',
              '& fieldset': {
                borderColor: 'grey.300',
              },
            },
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="caption" sx={{ color: COLOR_GREY_600, display: 'block' }}>
          Keep your API key secure. Do not share it publicly.
        </Typography>
      </Grid>
    </>
  );

  return <CustomBasicCard title="API Key" icon={<KeyIcon />} content={content} />;
};

export default ApiKeyDisplayCard;
